import { Locale, SUPPORTED_LOCALES } from '../i18n';
import siteLogo from '../assets/logo_text.png';

export const SITE_NAME = 'CheckPoint Charly';
export const SITE_URL = 'https://checkpointcharly.bg';
export const CONTACT_EMAIL = 'check_charly@abv.bg';
export const LOGO_URL = new URL(siteLogo, SITE_URL).toString();

const LOCALE_TO_HREFLANG: Record<Locale, string> = {
  en: 'en',
  bg: 'bg',
};

const LOCALE_TO_BCP47: Record<Locale, string> = {
  en: 'en-US',
  bg: 'bg-BG',
};

const LOCALE_TO_NAME: Record<Locale, string> = {
  en: 'English',
  bg: 'Bulgarian',
};

export const DEFAULT_LOCALE: Locale = SUPPORTED_LOCALES[0];

const ROUTE_KEY_BY_PATH = {
  '/': 'home',
  '/about': 'about',
  '/menu': 'menu',
  '/gallery': 'gallery',
  '/contact': 'contact',
  '/privacy-policy': 'privacy',
  '/terms': 'terms',
} as const;

export type RouteKey = (typeof ROUTE_KEY_BY_PATH)[keyof typeof ROUTE_KEY_BY_PATH];

const FALLBACK_ROUTE: RouteKey = 'home';

const ensureLeadingSlash = (value: string) => (value.startsWith('/') ? value : `/${value}`);

const stripQueryAndHash = (pathname: string) => {
  const [path] = pathname.split(/[?#]/, 1);
  return path ?? pathname;
};

const normalizePathname = (pathname: string) => {
  const stripped = stripQueryAndHash(pathname || '/');
  let normalized = ensureLeadingSlash(stripped);

  if (normalized !== '/' && normalized.endsWith('/')) {
    normalized = normalized.replace(/\/+$/, '');
  }

  return normalized;
};

export const resolveRouteKey = (pathname: string): RouteKey => {
  const normalized = normalizePathname(pathname) as keyof typeof ROUTE_KEY_BY_PATH;
  return ROUTE_KEY_BY_PATH[normalized] ?? FALLBACK_ROUTE;
};

export const buildCanonicalUrl = (pathname: string, locale: Locale): string => {
  const normalized = normalizePathname(pathname);
  const url = new URL(normalized, SITE_URL);

  if (locale !== DEFAULT_LOCALE) {
    url.searchParams.set('lang', locale);
  }

  return url.toString();
};

export const buildAlternateLinks = (pathname: string) => {
  const normalized = normalizePathname(pathname);
  const alternates = SUPPORTED_LOCALES.map((locale) => ({
    hrefLang: LOCALE_TO_HREFLANG[locale],
    href: buildCanonicalUrl(normalized, locale),
  }));

  const defaultHref = buildCanonicalUrl(normalized, DEFAULT_LOCALE);

  return [...alternates, { hrefLang: 'x-default', href: defaultHref }];
};

export const getOgLocale = (locale: Locale) => (locale === 'bg' ? 'bg_BG' : 'en_US');

export const getLanguageCode = (locale: Locale) => LOCALE_TO_BCP47[locale] ?? 'en-US';

const AVAILABLE_LANGUAGE_CODES = SUPPORTED_LOCALES.map((locale) => LOCALE_TO_BCP47[locale]);

const AVAILABLE_LANGUAGE_NAMES = SUPPORTED_LOCALES.map((locale) => LOCALE_TO_NAME[locale]);

export const buildStructuredData = (locale: Locale, canonicalUrl: string, title: string, description: string) => {
  const languageCode = getLanguageCode(locale);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${SITE_URL}#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: LOGO_URL,
      email: CONTACT_EMAIL,
      contactPoint: [
        {
          '@type': 'ContactPoint',
          email: CONTACT_EMAIL,
          contactType: 'customer support',
          availableLanguage: AVAILABLE_LANGUAGE_NAMES,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}#website`,
      url: SITE_URL,
      name: SITE_NAME,
      inLanguage: AVAILABLE_LANGUAGE_CODES,
      publisher: {
        '@id': `${SITE_URL}#organization`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': canonicalUrl,
      url: canonicalUrl,
      name: title,
      description,
      inLanguage: languageCode,
      isPartOf: {
        '@id': `${SITE_URL}#website`,
      },
      about: {
        '@id': `${SITE_URL}#organization`,
      },
    },
  ];

  return JSON.stringify(structuredData, null, 2);
};
